import { z } from 'zod';
import { EmailProvider } from '../contracts/email.js';
import { TimestampSchema } from '../primitives.js';
import { UiLocale } from '../uiLocale.js';
import { EmailNotificationStatus } from './emailNotification.js';
/**
 * `authEmails/{authEmailId}` — the account-email ledger (doc 88 D6, D13).
 *
 * Same engine and same claim-first discipline as the owner ledger
 * (`emailNotification.ts`), but its own collection and its own kinds: these
 * emails are about a person signing in, not about a business, so there is no
 * business to hang them under and no appointment to key them by.
 *
 * Rows exist only for emails we actually tried to send. Throttled, refused or
 * unknown-address requests write nothing (doc 88 D13): otherwise whoever calls
 * the open reset callable would decide how many rows we keep. A row holds the
 * address — never the link or its code, which are bearer secrets — and expires
 * after 30 days through the Firestore TTL policy on `expireAt`.
 *
 * Server-written entirely; no client reads or writes it.
 */
export const AuthEmailKind = z.enum([
    /** "Confirm your email" after sign-up, or on resend. */
    'auth_verify_email',
    /** "Choose a new password" from the forgot-password page. */
    'auth_password_reset',
]);
/** Engine-level reasons only: the account policy's refusals write no row. */
export const AuthEmailSuppressReason = z.enum(['no_address', 'invalid_address', 'no_provider']);
export const AuthEmailSchema = z.object({
    kind: AuthEmailKind,
    uid: z.string().min(1),
    to: z.string().max(254).default(''),
    language: UiLocale,
    provider: EmailProvider.nullable().default(null),
    status: EmailNotificationStatus,
    suppressReason: AuthEmailSuppressReason.nullable().default(null),
    providerMessageId: z.string().nullable().default(null),
    error: z.string().max(500).nullable().default(null),
    claimedAt: TimestampSchema,
    sentAt: TimestampSchema.optional(),
    /** TTL field: the row is deleted by Firestore some time after this. */
    expireAt: TimestampSchema,
});
/** How long a ledger row is kept (doc 88 D13). */
export const AUTH_EMAIL_LEDGER_TTL_DAYS = 30;
/**
 * Deterministic per user, kind and minute: a double click — or a client retry
 * of the same request — claims the same id and sends once.
 */
export function authEmailId(uid, kind, at) {
    return `${uid}_${kind}_${Math.floor(at.getTime() / 60_000)}`;
}
//# sourceMappingURL=authEmail.js.map